import React, { Component } from "react";
import { Chip, fmtMins, KpiTile } from "./preScreeningComp";
import { T, URGENCY } from "./preScreeningConfig";
import { getViewPatientData } from "../../lib";

export default class SupervisorView extends Component {
    constructor(props) {
        super(props);
        this.state = {
            kpis: null,
            loading: false,
            error: null,
        };
        this.tickInterval = null;
        this.tickState = 0;
    }

    componentDidMount() {
        this.fetchKpis();
        this.tickInterval = setInterval(() => {
            this.tickState += 1;
            this.forceUpdate();
        }, 30000);
    }

    componentWillUnmount() {
        clearInterval(this.tickInterval);
    }

    fetchKpis = async () => {
        try {
            this.setState({ loading: true, error: null });
            const data = await getViewPatientData({
                url: `${this.props.settings.server.screeningUrl}/ecm/pre-screening/kpis`,
                server: this.props.settings.server,
            });
            this.setState({ kpis: data, loading: false });
        } catch (error) {
            console.error("KPI fetch failed:", error);
            this.setState({ error: error.message, loading: false });
        }
    };

    render() {
        const { onJumpToTriage, queue = [] } = this.props;
        const { kpis, loading, error } = this.state;

        if (loading) {
            return (
                <section style={{ gridColumn: "1 / -1" }}>
                    <div style={{ background: T.card, border: `1px dashed ${T.line}`, borderRadius: 10, padding: 32, textAlign: "center", color: T.mute, fontSize: 14 }}>
                        <i className="fa fa-spinner fa-spin" /> Loading supervisor dashboard…
                    </div>
                </section>
            );
        }

        if (error) {
            return (
                <section style={{ gridColumn: "1 / -1" }}>
                    <div style={{ background: T.card, border: `1px dashed ${T.line}`, borderRadius: 10, padding: 32, textAlign: "center", color: T.immediate, fontSize: 14 }}>
                        Failed to load KPIs: {error}
                        <button onClick={this.fetchKpis} style={{ marginLeft: 12, background: T.pine, color: "#fff", border: "none", borderRadius: 6, padding: "5px 12px", fontSize: 13, cursor: "pointer" }}>
                            Retry
                        </button>
                    </div>
                </section>
            );
        }

        if (!kpis) {
            return (
                <section style={{ gridColumn: "1 / -1" }}>
                    <div style={{ background: T.card, border: `1px dashed ${T.line}`, borderRadius: 10, padding: 32, textAlign: "center", color: T.mute, fontSize: 14 }}>
                        No data available.
                    </div>
                </section>
            );
        }

        const {
            openForReview,
            slaBreaches,
            outreachStarted,
            avgTimeToDecisionMinutes,
            decisions,
            caseloadByCareManager = [],
            urgencyMix = {},
            needSignals = {},
        } = kpis;

        const domainMax = Math.max(1, needSignals.clinical || 0, needSignals.behavioral || 0, needSignals.social || 0);

        const domains = [
            { key: "clinical", label: "Clinical", color: T.clinical },
            { key: "behavioral", label: "Behavioral", color: T.behavioral },
            { key: "social", label: "Social / SDOH", color: T.social },
        ].map((d) => ({ ...d, count: needSignals[d.key] || 0 }));

        const urgencyMixTotal = Object.values(urgencyMix).reduce((s, n) => s + n, 0);

        return (
            <section style={{ gridColumn: "1 / -1", minWidth: 0 }}>
                {/* KPI row */}
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
                    <KpiTile label="Open for review" value={openForReview} sub="awaiting a human decision" />
                    <KpiTile
                        label="SLA"
                        value={slaBreaches}
                        color={slaBreaches ? T.immediate : T.ok}
                        sub={slaBreaches ? "needs reassignment now" : "all within target"}
                    />
                    <KpiTile label="Outreach started" value={outreachStarted} sub="this session" color={T.ok} />
                    <KpiTile
                        label="Avg time to decision"
                        value={fmtMins(avgTimeToDecisionMinutes)}
                        sub={`${decisions} decision${decisions !== 1 ? "s" : ""}`}
                    />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))", gap: 14 }}>
                    {/* Caseload by care manager */}
                    <div style={{ background: T.card, border: `1px solid ${T.line}`, borderRadius: 10, padding: 14 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: T.mute, textTransform: "uppercase", marginBottom: 10 }}>Caseload by care manager</div>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                            <thead>
                                <tr style={{ color: T.mute, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6 }}>
                                    <th style={{ textAlign: "left", padding: "4px 0", fontWeight: 600 }}>Care manager</th>
                                    <th style={{ textAlign: "right", padding: "4px 0", fontWeight: 600 }}>Open</th>
                                    <th style={{ textAlign: "right", padding: "4px 0", fontWeight: 600 }}>Urgent</th>
                                    <th style={{ textAlign: "right", padding: "4px 0", fontWeight: 600 }}>Resolved</th>
                                </tr>
                            </thead>
                            <tbody>
                                {caseloadByCareManager.filter((row) => row.careManager).map((row) => (
                                    <tr key={row.careManagerId} style={{ borderTop: `1px solid ${T.line}` }}>
                                        <td style={{ padding: "7px 0", fontWeight: 600 }}>{row.careManager}</td>
                                        <td style={{ textAlign: "right" }}>{row.open}</td>
                                        <td style={{ textAlign: "right", color: row.urgent ? T.immediate : T.mute, fontWeight: row.urgent ? 700 : 400 }}>{row.urgent}</td>
                                        <td style={{ textAlign: "right", color: T.mute }}>{row.resolved}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Urgency mix + domain signals */}
                    <div style={{ background: T.card, border: `1px solid ${T.line}`, borderRadius: 10, padding: 14 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: T.mute, textTransform: "uppercase", marginBottom: 10 }}>Open queue: urgency mix</div>
                        {urgencyMixTotal === 0 ? (
                            <div style={{ fontSize: 13, color: T.mute }}>Queue is clear.</div>
                        ) : (
                            <div style={{ display: "flex", height: 18, borderRadius: 6, overflow: "hidden", border: `1px solid ${T.line}` }}>
                                {Object.entries(urgencyMix).map(([k, count]) => {
                                    // normalize API key e.g. "ActNow" → "actnow" to match URGENCY keys
                                    const uKey = k.toLowerCase().replace(/\s/g, "_");
                                    const u = URGENCY[uKey] || URGENCY.routine;
                                    if (!count) return null;
                                    return (
                                        <div key={k} title={`${u.label || k}: ${count}`} style={{ flex: count, background: u.color }} />
                                    );
                                })}
                            </div>
                        )}
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
                            {Object.entries(urgencyMix).map(([k, count]) => {
                                if (!count) return null;
                                const uKey = k.toLowerCase().replace(/\s/g, "_");
                                console.log("uKey", uKey)
                                const u = URGENCY[uKey] || URGENCY.routine;
                                return (
                                    <span key={k} style={{ fontSize: 12, color: T.mute }}>
                                        <span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, background: u.color, marginRight: 4 }} />
                                        {u.label || k} ({count})
                                    </span>
                                );
                            })}
                        </div>

                        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: T.mute, textTransform: "uppercase", margin: "16px 0 8px" }}>Active need signals</div>
                        {domains.map((d) => (
                            <div key={d.key} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                                <span style={{ fontSize: 12.5, width: 105, flexShrink: 0 }}>{d.label}</span>
                                <div style={{ flex: 1, height: 12, background: T.paper, borderRadius: 4, overflow: "hidden" }}>
                                    <div style={{ width: `${(d.count / domainMax) * 100}%`, height: "100%", background: d.color, transition: "width .3s" }} />
                                </div>
                                <span style={{ fontSize: 12.5, fontWeight: 700, width: 22, textAlign: "right" }}>{d.count}</span>
                            </div>
                        ))}
                        <div style={{ fontSize: 11.5, color: T.mute, marginTop: 4 }}>
                            Findings across open cases — a rising Social bar means your team needs community resource capacity, not just clinical follow-up.
                        </div>

                    </div>
                </div>
                {/* SLA board */}
                <div style={{ background: T.card, border: `1px solid ${T.line}`, borderRadius: 10, padding: 14, marginTop: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: T.mute, textTransform: "uppercase", marginBottom: 10 }}>
                        Open cases by wait time {slaBreaches > 0 && <span style={{ color: T.immediate }}>· {slaBreaches} over target</span>}
                    </div>
                    {queue.length === 0 && <div style={{ fontSize: 13, color: T.mute }}>Nothing waiting. 🎉</div>}
                    {queue.map((q) => {
                        const u = URGENCY[q.triage.urgency] || URGENCY.routine;
                        const overdue = q.slaBreached;
                        const waitedMin = q.waitingMinutes || 0;
                        return (
                            <div key={q.id} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "8px 0", borderTop: `1px solid ${T.line}` }}>
                                <Chip color={u.color} filled>{u.label}</Chip>
                                <strong style={{ fontSize: 13.5 }}>{q.enc.patientName}</strong>
                                <span style={{ fontFamily: T.mono, fontSize: 12, color: T.mute }}>{q.enc.mrn}</span>
                                <span style={{ fontSize: 12.5, color: T.mute }}>CM {q.rosterRow?.careManager || "—"}</span>
                                <span style={{ marginLeft: "auto", fontSize: 12.5, fontWeight: 700, color: overdue ? T.immediate : T.mute }}>
                                    waiting {fmtMins(waitedMin)}{overdue ? " — OVER TARGET" : ""}
                                </span>
                                <button
                                    onClick={onJumpToTriage}
                                    style={{ background: "none", border: `1px solid ${T.line}`, borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 600, color: T.pine }}
                                >
                                    Open in triage
                                </button>
                            </div>
                        );
                    })}
                </div>
            </section>
        );
    }
}